<script setup>
// Config do node "Etiquetar conversa": a IA decide QUANDO etiquetar pela
// conversa; aqui você fixa QUAIS etiquetas ela pode usar (das existentes na
// conta) e uma orientação opcional de quando aplicar cada uma. Vira a tool
// aplicar_etiqueta dinâmica (api_service#preset_tool_config).
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();
const store = useStore();

const accountLabels = computed(() => store.getters['labels/getLabels'] || []);
const allowed = computed(() => props.data.allowedLabels || []);

onMounted(() => {
  store.dispatch('labels/get');
});

// Regra por etiqueta em frase fixa: aplique [etiqueta] quando conter [___].
// A parte fixa fica cinza; o que a pessoa digita fica em destaque.
const labelRules = computed(() => props.data.labelRules || {});

const toggleLabel = title => {
  const selected = allowed.value.includes(title);
  const next = selected
    ? allowed.value.filter(l => l !== title)
    : [...allowed.value, title];
  const rules = { ...labelRules.value };
  if (selected) delete rules[title];
  emit('update', { allowedLabels: next, labelRules: rules });
};

const onRule = (title, event) => {
  const rules = { ...labelRules.value, [title]: event.target.value };
  if (!rules[title].trim()) delete rules[title];
  emit('update', { labelRules: rules });
};

const onGuidance = event => emit('update', { guidance: event.target.value });
</script>

<template>
  <div class="p-4">
    <div class="mb-1 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="i-lucide-tag text-n-slate-11" />
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.APPLY_LABEL.TITLE') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>
    <p class="mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.APPLY_LABEL.SUBTITLE') }}
    </p>

    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.APPLY_LABEL.ALLOWED') }}
    </label>
    <div v-if="accountLabels.length" class="mb-1 flex flex-wrap gap-1.5">
      <button
        v-for="label in accountLabels"
        :key="label.id"
        type="button"
        class="rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
        :class="
          allowed.includes(label.title)
            ? 'border-n-teal-8 bg-n-teal-3 text-n-teal-11'
            : 'border-n-weak text-n-slate-10 hover:text-n-slate-12'
        "
        @click="toggleLabel(label.title)"
      >
        {{ label.title }}
      </button>
    </div>
    <p v-else class="mb-1 text-xs text-n-amber-11">
      {{ t('AI_AGENTS.FLOW.APPLY_LABEL.EMPTY') }}
    </p>
    <p class="mb-3 text-[11px] text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.APPLY_LABEL.ALLOWED_HINT') }}
    </p>

    <!-- Frase fixa por etiqueta: aplique [chip] quando conter [o que digitar] -->
    <div v-if="allowed.length" class="mb-3">
      <label class="mb-1 block text-xs text-n-slate-10">
        {{ t('AI_AGENTS.FLOW.APPLY_LABEL.RULES') }}
      </label>
      <div class="flex flex-col gap-1.5">
        <div
          v-for="title in allowed"
          :key="title"
          class="flex flex-wrap items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-xs"
        >
          <span class="text-n-slate-10">
            {{ t('AI_AGENTS.FLOW.APPLY_LABEL.RULE_PREFIX') }}
          </span>
          <span
            class="max-w-32 truncate rounded-full border border-n-teal-8 bg-n-teal-3 px-2 py-0.5 font-medium text-n-teal-11"
            :title="title"
          >
            {{ title }}
          </span>
          <span class="text-n-slate-10">
            {{ t('AI_AGENTS.FLOW.APPLY_LABEL.RULE_WHEN') }}
          </span>
          <input
            :value="labelRules[title] || ''"
            type="text"
            class="reset-base min-w-28 flex-1 rounded-md border border-n-weak bg-n-background px-2 py-1 text-xs font-medium text-n-amber-11"
            :placeholder="t('AI_AGENTS.FLOW.APPLY_LABEL.RULE_PH')"
            @input="onRule(title, $event)"
          />
        </div>
      </div>
      <p class="mt-1 text-[11px] text-n-slate-10">
        {{ t('AI_AGENTS.FLOW.APPLY_LABEL.RULES_HINT') }}
      </p>
    </div>

    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.APPLY_LABEL.GUIDANCE') }}
    </label>
    <textarea
      :value="data.guidance || ''"
      rows="2"
      class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm text-n-slate-12"
      :placeholder="t('AI_AGENTS.FLOW.APPLY_LABEL.GUIDANCE_PH')"
      @input="onGuidance"
    />

    <label class="mb-3 flex items-center justify-between gap-2">
      <span class="text-xs text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.APPLY_LABEL.ENABLED') }}
      </span>
      <input
        type="checkbox"
        :checked="data.enabled !== false"
        class="rounded border-n-weak"
        @change="emit('update', { enabled: $event.target.checked })"
      />
    </label>

    <button
      class="w-full rounded-lg border border-n-ruby-6 py-1.5 text-xs font-medium text-n-ruby-11 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.APPLY_LABEL.REMOVE') }}
    </button>
  </div>
</template>
